import React, { useState, useCallback } from "react";
import { Eye, CheckCircle, XCircle, RotateCcw, FileText } from "lucide-react";
import { useIntl } from "react-intl";
import Toast from "../../helpers/Toast";
import ButtonComponent from "../ButtonComponent";
import AssistanceCheckListTaskDetailsComponent from "./AssistanceCheckListTaskDetailsComponent";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { AssistanceCheckListTaskDTO } from "../../models/AssistanceCheckListTask/AssistanceCheckListTaskDTO";
import { AssistanceCheckListTaskService } from "../../services/AssistanceCheckListTaskService";
import { AssistanceCheckListService } from "../../services/AssistanceCheckListService";
import AssistanceCheckListTaskMotiveModalComponent from "./AssistanceCheckListTaskMotiveModalComponent";
import AssistanceChekListTaskObservationModalComponent from "./AssistanceChekListTaskObservationModalComponent";

interface Props {
  assistance: AssistanceDTO;
  setAssistance: (a: AssistanceDTO) => void;
  loading: boolean;
  userCanEdit?: boolean;
}

const AssistanceCheckListTaskListComponent: React.FC<Props> = ({
  assistance,
  setAssistance,
  loading,
  userCanEdit,
}) => {
  const intl = useIntl();
  const taskService = new AssistanceCheckListTaskService();
  const checklistService = new AssistanceCheckListService();

  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [modalType, setModalType] = useState<"view" | "observation" | null>(null);

  const tasks: AssistanceCheckListTaskDTO[] =
    assistance.assistanceCheckList?.assistanceCheckListTasks ?? [];

  const executedState = "EXECUTADA_COM_SUCESSO";
  const notExecutedState = "NAO_EXECUTADA";
  const executedWithObservationState = "EXECUTADA_COM_OBSERVAÇOES";
  const notStartedState = "CHECKLIST_TASK_POR_INICIAR";

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorTaskId, setErrorTaskId] = useState<number | null>(null);
  const [errorSaving, setErrorSaving] = useState(false);

  const [obsModalOpen, setObsModalOpen] = useState(false);
  const [obsTaskId, setObsTaskId] = useState<number | null>(null);
  const [obsSaving, setObsSaving] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTaskId, setDetailsTaskId] = useState<number | null>(null);

  const openErrorModal = (taskId: number) => {
    setErrorTaskId(taskId);
    setErrorModalOpen(true);
  };

  const openObsModal = (taskId: number) => {
    setObsTaskId(taskId);
    setObsModalOpen(true);
  };

  const openDetails = (id: number) => {
    setDetailsTaskId(id);
    setDetailsOpen(true);
  };


  const refreshChecklist = useCallback(
    async (successMsg?: string) => {
      if (!assistance.assistanceCheckList?.id) return;

      const resp = await checklistService.GetById(assistance.assistanceCheckList.id);
      if (!resp.success) {
        Toast.Show("error", resp.message || "Erro a carregar checklist.");
        return;
      }

      setAssistance({ ...assistance, assistanceCheckList: resp.obj });
      if (successMsg) Toast.Show("success", successMsg);
    },
    [assistance, checklistService, setAssistance]
  );

  const handleAction = async (
    taskId: number,
    action: "CONCLUDE" | "ERROR" | "OBSERVATION" | "UNDO",
    observation = "",
    motive = ""
  ) => {
    if (!userCanEdit) {
      Toast.Show("error", intl.formatMessage({ id: "Checlist.Assistance.NotEditable" }));
      return;
    }
    const dto = { id: taskId, observation, motive };

    let resp;
    switch (action) {
      case "CONCLUDE":
        resp = await taskService.ConcludeTask(dto);
        break;
      case "ERROR":
        resp = await taskService.ConcludeTaskWithError(dto);
        break;
      case "OBSERVATION":
        resp = await taskService.ConcludeTaskWithObservation(dto);
        break;
      case "UNDO":
        resp = await taskService.UndoTaskAction(dto);          // NOVO
        break;
      default:
        resp = { success: false, message: "Ação não implementada." };
    }

    if (!resp.success) {
      Toast.Show("error", resp.message);
      return;
    }
    await refreshChecklist(resp.message);
  };

  const sortedTasks = [...tasks].sort((a, b) => a.order - b.order);
  const selectedTask = tasks.find((t) => t.id === selectedTaskId) ?? null;

  const renderStatusIcon = (IconComponent: React.ElementType, color: string, taskId: number) => (
    <>
      <IconComponent color={color} />
      {userCanEdit && (
        <RotateCcw
          style={{ cursor: "pointer" }}
          onClick={() => handleAction(taskId, "UNDO")}
        />
      )}
    </>
  );


  return (
    <div className="checklist-task-list">
      {sortedTasks.map((task) => {
        const status = task.status?.statusEnumValue;

        return (
          <div
            key={task.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <span style={{ fontWeight: "bold", width: 40 }}>{task.order}.</span>
            <span style={{ flex: 1 }}>{task.title}</span>

            <FileText
              style={{ cursor: "pointer" }}
              onClick={() => openDetails(task.id)}
            />

            {(!status || status === notStartedState) && userCanEdit && (
              <>
                <CheckCircle
                  color="green"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAction(task.id, "CONCLUDE")}
                />
                <XCircle
                  color="red"
                  style={{ cursor: "pointer" }}
                  onClick={() => openErrorModal(task.id)}
                />
                <Eye
                  color="orange"
                  style={{ cursor: "pointer" }}
                  onClick={() => openObsModal(task.id)}
                />
              </>
            )}


            {status === executedState && renderStatusIcon(CheckCircle, "green", task.id)}
            {status === notExecutedState && renderStatusIcon(XCircle, "red", task.id)}
            {status === executedWithObservationState && renderStatusIcon(Eye, "orange", task.id)}

          </div>
        );
      })}

      <AssistanceCheckListTaskMotiveModalComponent
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={async (motive: string) => {
          if (!errorTaskId) return;

          setErrorSaving(true);
          const dto = { id: errorTaskId, motive };
          const resp = await taskService.ConcludeTaskWithError(dto);
          setErrorSaving(false);

          if (!resp.success) {
            Toast.Show("error", resp.message);
            return;
          }

          setErrorModalOpen(false);
          await refreshChecklist(resp.message);
        }}
        taskTitle={tasks.find((t) => t.id === errorTaskId)?.title}
        loading={errorSaving}
      />

      <AssistanceChekListTaskObservationModalComponent
        isOpen={obsModalOpen}
        onClose={() => setObsModalOpen(false)}
        taskTitle={tasks.find((t) => t.id === obsTaskId)?.title}
        loading={obsSaving}
        onConfirm={async (observation: string) => {
          if (!obsTaskId) return;

          setObsSaving(true);
          const dto = { id: obsTaskId, observation };
          const resp = await taskService.ConcludeTaskWithObservation(dto);
          setObsSaving(false);

          if (!resp.success) {
            Toast.Show("error", resp.message);
            return;
          }

          setObsModalOpen(false);
          await refreshChecklist(resp.message);
        }}
      />

      <AssistanceCheckListTaskDetailsComponent
        open={detailsOpen}
        taskId={detailsTaskId ?? 0}
        onClose={() => setDetailsOpen(false)}
        onSave={async (newObs: string) => {
          await refreshChecklist();          // refresca lista após editar
        }}
      />

    </div>
  );
};

export default AssistanceCheckListTaskListComponent;

